import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  getOrderById,
  getOrdersByPage,
  getNewOrderSummary,
  updateOrderStatus,
  createSupplierNoteApi,
  cancelOrderStatus,
  orderProceed,
  packedOrderStatus,
  shippedOrderStatus,
  getActiveOrders,
  getNewOrderCount,
  updateOrderDetails,
} from '../../ApiServices/OrdersService';
import { t } from 'i18next';
import { handleResponse } from '../../ApiServices/ApiHandler';

export const initialState = {
  value: { totalCount: -1, data: [{}] },
  activeOrdersvalue: { totalCount: -1, data: [{}] },
  loading: false,
  error: null,
  selectedValue: null,
  rowsPerPage: 10,
  pageNo: 0,
  filter: {},
  newOrderCount: {},
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState: initialState,
  reducers: {
    clearOrdersState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.activeOrdersvalue = initialState.activeOrdersvalue;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.filter = initialState.filter;
      state.newOrderCount = initialState.newOrderCount;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrders.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchOrders.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchOrderById.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
      state.message = action?.payload?.data;
    });
    builder.addCase(fetchOrderById.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
      state.error = action?.payload?.data;
    });
    builder.addCase(fetchActiveOrders.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.activeOrdersvalue = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchActiveOrders.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.activeOrdersvalue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchNewOrderCount.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.newOrderCount = action?.payload?.data;
    });
    builder.addCase(fetchNewOrderCount.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.error = action?.payload?.data;
      state.newOrderCount = initialState.newOrderCount;
    });
  },
});

export const ordersState = (state: RootState) => state?.orders;
export const { clearOrdersState } = ordersSlice.actions;
export default ordersSlice.reducer;

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_ORDERS') }));
      return fulfillWithValue(await getOrdersByPage(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchActiveOrders = createAsyncThunk(
  'orders/fetchActiveOrders',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_ACTIVE_ORDERS') }));
      return fulfillWithValue(await getActiveOrders(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_ORDER_DETAILS') }));
      return fulfillWithValue(await getOrderById(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const modifyOrderStatus = createAsyncThunk(
  'orders/modifyOrderStatus',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('UPDATING_ORDER_STATUS') }));
      let response: any = await updateOrderStatus(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('ORDER_STATUS_CHANGED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(
          showErrorSnackbar({ message: t('ORDER_STATUS_CHANGE_FAILED') })
        );
        return rejectWithValue(response);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadOrders = createAsyncThunk(
  'orders/downloadOrders',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_ORDERS') }));
      let response: any = await getOrdersByPage(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadNewOrderSummary = createAsyncThunk(
  'orders/downloadNewOrderSummary',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_NEW_ORDER_SUMMARY') }));
      let response: any = await getNewOrderSummary(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const createSupplierNote = createAsyncThunk(
  'orders/createSupplierNote',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('ADDING_NEW_NOTE') }));
      let response = await createSupplierNoteApi(payload);
      dispatch(
        showSuccessSnackbar({ message: t('NOTE_CREATED_SUCCESSFULLY') })
      );
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('CANCELLING_ORDER_STATUS') }));
      let response: any = await cancelOrderStatus(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('ORDER_CANCELLED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(showErrorSnackbar({ message: t('ORDER_CANCEL_FAILED') }));
        return rejectWithValue(response);
      }
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const proceedOrder = createAsyncThunk(
  'orders/proceedOrder',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('PROCEDDING_ORDER') }));
      let response: any = await orderProceed(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(showSuccessSnackbar({ message: t('ORDER_SPLIT_SUCCESS') }));
        return fulfillWithValue(response);
      } else {
        dispatch(showErrorSnackbar({ message: t('ORDER_SPLIT_FAILED') }));
        return rejectWithValue(response);
      }
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const packedOrder = createAsyncThunk(
  'orders/packedOrder',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('PACKED_ORDER_STATUS') }));
      let response: any = await packedOrderStatus(payload);
      return await handleResponse(
        response,
        fulfillWithValue,
        rejectWithValue,
        dispatch
      );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const shippedOrder = createAsyncThunk(
  'orders/shippedOrder',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('SHIPPED_ORDER_STATUS') }));
      let response: any = await shippedOrderStatus(payload);
      return await handleResponse(
        response,
        fulfillWithValue,
        rejectWithValue,
        dispatch
      );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchNewOrderCount = createAsyncThunk(
  'orders/fetchNewOrderCount',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      //dispatch(showLoader({ message: t('DELETING_MESSAGE') }));
      return fulfillWithValue(await getNewOrderCount(payload));
    } catch (error) {
      //dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      //dispatch(hideLoader());
    }
  }
);

export const downloadActiveOrders = createAsyncThunk(
  'orders/downloadActiveOrders',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_ORDERS') }));
      let response: any = await getActiveOrders(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const updateStoreDetails = createAsyncThunk(
  'stores/updateStoreDetails',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_SHIP_TO_DETAILS') }));
      let response = await updateOrderDetails(payload);
      dispatch(
        showSuccessSnackbar({
          message: t('UPDATED_SHIPPING_DETAILS_SUCCESSFULLY'),
        })
      );
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadOrderDetails = createAsyncThunk(
  'orders/downloadActiveOrders',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_ORDERS') }));
      let response: any = await getOrderById(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
